import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { HOME_MEDIA, getApiBaseUrl, mediaUrl, publicMediaSrc } from "../config/api";

const nav = [
  { to: "/", label: "Home" },
  { to: "/category", label: "Category" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About Us" },
  { to: "/deliveries", label: "Deliveries" },
  { to: "/contact", label: "Contact Us" },
];

function scrollToPageTop() {
  window.scrollTo(0, 0);
}

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function Header() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setContact(Array.isArray(list) && list[0] ? list[0] : null);
      })
      .catch(() => {
        if (!cancelled) setContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        ticking = false;
      });
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logoSrc = publicMediaSrc(contact?.siteLogo) || mediaUrl(HOME_MEDIA.logo);
  const siteName = String(contact?.siteName || "").trim();

  useEffect(() => {
    const href = publicMediaSrc(contact?.siteLogo);
    if (href) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = href;
    }

    if (
      location.pathname === "/" ||
      location.pathname === "/products" ||
      location.pathname === "/about" ||
      location.pathname === "/contact" ||
      location.pathname === "/deliveries" ||
      location.pathname.startsWith("/deliveries/") ||
      location.pathname === "/category" ||
      location.pathname.startsWith("/category/") ||
      /^\/products\/[^/]+\/[^/]+$/.test(location.pathname)
    ) {
      return;
    }

    const companyName = String(contact?.companyName || "").trim();
    if (!companyName) return;
    document.title = `${companyName} - Steel, Hardware, and Industrial Supply`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", `${companyName} — Steel, Hardware, and Industrial Supply.`);
    }
  }, [contact, location.pathname]);

  return (
    <header className={`site-header text-white ${scrolled ? "is-scrolled" : ""}`}>
      <div className="flex items-center justify-between gap-4 px-16 py-3 md:px-32 lg:px-40">
        <Link to="/" onClick={scrollToPageTop} className="flex min-w-0 items-center gap-3">
          <img
            src={logoSrc}
            alt={siteName ? "" : "Home"}
            className="h-10 w-10 object-contain invert md:h-12 md:w-12"
          />
          {siteName ? (
            <span className="min-w-0 text-[10px] font-semibold uppercase leading-tight tracking-[0.14em] text-white/90 sm:text-xs">
              {siteName}
            </span>
          ) : null}
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-6 lg:flex">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={scrollToPageTop}
              className={({ isActive }) =>
                `nav-link text-sm ${isActive ? "is-active text-white" : "text-white/70 hover:text-white"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/contact?intent=quote#contact-form"
            onClick={lockDoubleClick}
            className="quote-btn hidden rounded bg-at-gold px-3 py-2 text-xs font-semibold text-at-dark hover:bg-at-gold-hover sm:inline-flex"
          >
            Get a Quote <span className="btn-arrow">→</span>
          </Link>
          <button
            type="button"
            className="lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="block h-0.5 w-5 bg-white" />
            <span className="mt-1 block h-0.5 w-5 bg-white" />
            <span className="mt-1 block h-0.5 w-5 bg-white" />
          </button>
        </div>
      </div>

      {open && (
        <nav aria-label="Mobile" className="border-t border-white/10 px-16 py-3 lg:hidden">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => {
                setOpen(false);
                scrollToPageTop();
              }}
              className="block py-2 text-sm text-white/80"
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact?intent=quote#contact-form"
            onClick={(event) => {
              lockDoubleClick(event);
              setOpen(false);
            }}
            className="quote-btn mt-2 inline-flex rounded bg-at-gold px-3 py-2 text-xs font-semibold text-at-dark"
          >
            Get a Quote <span className="btn-arrow">→</span>
          </Link>
        </nav>
      )}
    </header>
  );
}

export default Header;
